import {
  Appointment,
  Invoice,
  InvoiceContextBuilder,
  Patient,
  ScopeDataBuilder,
} from '@principle-theorem/principle-core';
import {
  type IAppointment,
  type IInvoice,
  type IPatient,
  type IPrincipleMention,
  type IResolvedTemplateContext,
  type ITemplateContextOption,
  MentionResourceType,
  TemplateScope,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asDocRef,
  DATE_TIME_WITH_YEAR_FORMAT,
  filterUndefined,
  getDoc,
  type IProvider,
  multiFilter,
  snapshot,
  sortByCreatedAt,
  toMoment,
  type WithRef,
} from '@principle-theorem/shared';
import { memoize } from 'lodash';
import * as moment from 'moment-timezone';
import { map } from 'rxjs/operators';

/** Keeps the invoice picker short for patients with years of invoices. */
const invoiceTemplateWindow = moment.duration(12, 'months');

export class InvoiceMentionContextProvider
  implements IProvider<TemplateScope[], Promise<ITemplateContextOption[]>>
{
  constructor(
    private _mention: IPrincipleMention,
    private _appUrl: string
  ) {}

  canProvide(scopes: TemplateScope[]): boolean {
    const type = this._mention.resource.type;
    const isInvoiceScope = scopes.includes(TemplateScope.Invoice);
    const isInvoiceResource = type === MentionResourceType.Invoice;
    const isPatientResource = type === MentionResourceType.Patient;
    const isAppointmentResource = type === MentionResourceType.Appointment;
    return (
      isInvoiceScope &&
      (isInvoiceResource || isPatientResource || isAppointmentResource)
    );
  }

  async execute(): Promise<ITemplateContextOption[]> {
    const type = this._mention.resource.type;
    const isPatientResource = type === MentionResourceType.Patient;
    const isAppointmentResource = type === MentionResourceType.Appointment;

    if (isPatientResource) {
      const patient = await getDoc(
        asDocRef<IPatient>(this._mention.resource.ref)
      );
      return this._resolveInvoiceContextsFromPatient(patient);
    }

    if (isAppointmentResource) {
      const appointment = await getDoc(
        asDocRef<IAppointment>(this._mention.resource.ref)
      );
      const patient = await snapshot(Appointment.patient$(appointment));

      if (!appointment.invoiceRef) {
        return this._resolveInvoiceContextsFromPatient(patient);
      }
      return this._resolveInvoiceContextFromAppointment(appointment);
    }

    const invoice = await getDoc(
      asDocRef<IInvoice>(this._mention.resource.ref)
    );
    return [this._resolveInvoiceContext(invoice)];
  }

  private async _resolveInvoiceContextFromAppointment(
    appointment: WithRef<IAppointment>
  ): Promise<ITemplateContextOption[]> {
    const invoice = await snapshot(
      Appointment.invoice$(appointment).pipe(filterUndefined())
    );

    return [this._resolveInvoiceContext(invoice)];
  }

  private async _resolveInvoiceContextsFromPatient(
    patient: WithRef<IPatient>
  ): Promise<ITemplateContextOption[]> {
    const createdAfter = moment().subtract(invoiceTemplateWindow);
    const invoices = await snapshot(
      Patient.withPatientRelationships$(
        patient,
        [PatientRelationshipType.DuplicatePatient],
        Patient.invoices$
      ).pipe(
        multiFilter(
          (invoice) =>
            !Invoice.isCancelledInvoice(invoice) &&
            toMoment(invoice.createdAt).isAfter(createdAfter)
        ),
        map((results) => results.sort(sortByCreatedAt))
      )
    );

    return invoices.map((invoice) => this._resolveInvoiceContext(invoice));
  }

  private _resolveInvoiceContext(
    invoice: WithRef<IInvoice>
  ): ITemplateContextOption {
    const label = `${invoice.status.toUpperCase()} - ${
      invoice.practice.name
    } - ${toMoment(invoice.createdAt).format(DATE_TIME_WITH_YEAR_FORMAT)}`;

    return {
      label,
      scope: TemplateScope.Invoice,
      resolve: memoize(() => this._buildInvoiceContext(invoice)),
    };
  }

  private async _buildInvoiceContext(
    invoice: WithRef<IInvoice>
  ): Promise<IResolvedTemplateContext> {
    const scopeData = await ScopeDataBuilder.buildInvoiceScopeData(
      invoice,
      this._appUrl
    );
    const context = new InvoiceContextBuilder(scopeData).build();
    return { context, scopeData };
  }
}
